import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

import { fastTransition } from '../../styles/effects'
import { applyTextStyle } from '../../styles/typography'
import { Icon } from '../icon'
import Switch from '../formControl/Switch'
import { StyledStatusTag } from '../tag/StatusTag'
import type { ParentSelectionState } from '../../utils/filterSelectionMath'
import type { FilterMode, FilterOption } from './Filter'

interface IProps<V> {
  option: FilterOption<V>
  mode: FilterMode
  depth: number
  isParent: boolean
  isExpanded?: boolean
  parentState?: ParentSelectionState
  selected: boolean
  childSelectedCount?: number
  isActive?: boolean
  onToggle: () => void
  onToggleExpand?: () => void
}

const FilterOptionRow = <V,>({
  option,
  mode,
  depth,
  isParent,
  isExpanded,
  parentState,
  selected,
  childSelectedCount,
  isActive,
  onToggle,
  onToggleExpand,
}: IProps<V>): JSX.Element => {
  const theme = useContext(ThemeContext)
  const isToggle = option.kind === 'toggle'
  const isDisabled = !!option.disabled
  const isSelectedVisually = isParent ? parentState === 'all' : selected

  const indicatorState: ParentSelectionState = isParent
    ? parentState ?? 'none'
    : selected
    ? 'all'
    : 'none'

  const handleRowClick = () => {
    if (isDisabled) return
    onToggle()
  }

  const handleChevronClick = (event: React.MouseEvent) => {
    if (!onToggleExpand) return
    event.stopPropagation()
    onToggleExpand()
  }

  return (
    <Row
      role="option"
      aria-selected={isSelectedVisually}
      aria-disabled={isDisabled}
      aria-checked={
        mode === 'multi' && isParent && parentState === 'some'
          ? 'mixed'
          : isSelectedVisually
      }
      data-active={isActive ? 'true' : undefined}
      data-depth={depth}
      disabled={isDisabled}
      onClick={handleRowClick}
      isSelected={isSelectedVisually}
      isToggle={isToggle}
    >
      <Indent depth={depth} />
      {isToggle ? (
        <Switch isOn={selected} size="small" disabled={isDisabled} />
      ) : mode === 'single' ? (
        <Radio isSelected={selected} disabled={isDisabled} aria-hidden="true" />
      ) : (
        <CheckBox
          state={indicatorState}
          disabled={isDisabled}
          aria-hidden="true"
        >
          {indicatorState === 'all' && (
            <Icon type="Check" size={12} color={theme.colors.lightest} />
          )}
          {indicatorState === 'some' && (
            <Icon type="Dash" size={12} color={theme.colors.lightest} />
          )}
        </CheckBox>
      )}
      <Label isSelected={isSelectedVisually} disabled={isDisabled}>
        {option.label}
      </Label>
      {isParent && childSelectedCount !== undefined && childSelectedCount > 0 && (
        <CountBadge captionColor="primary" color="primaryLight">
          {childSelectedCount}
        </CountBadge>
      )}
      {isParent && onToggleExpand && (
        <ChevronButton
          type="button"
          onClick={handleChevronClick}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          tabIndex={-1}
        >
          <Icon
            type={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={12}
            color={theme.colors.text}
          />
        </ChevronButton>
      )}
    </Row>
  )
}

const Row = styled.div<{
  isSelected: boolean
  disabled?: boolean
  isToggle?: boolean
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  user-select: none;
  transition: background-color ${fastTransition};
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.primaryLight : 'transparent'};

  &:hover,
  &[data-active='true'] {
    background-color: ${({ theme }) => theme.colors.primaryLight};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      opacity: 0.5;
      &:hover,
      &[data-active='true'] {
        background-color: transparent;
      }
    `}
`

const Indent = styled.span<{ depth: number }>`
  display: inline-block;
  width: ${({ depth }) => depth * 16}px;
  flex-shrink: 0;
`

const CheckBox = styled.span<{
  state: ParentSelectionState
  disabled?: boolean
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  background-color: ${({ theme, state }) =>
    state === 'none' ? 'transparent' : theme.colors.primary};
  transition: background-color ${fastTransition}, border-color ${fastTransition};

  ${({ disabled, theme }) =>
    disabled &&
    css`
      border-color: ${theme.colors.textLighter};
      background-color: transparent;
    `}
`

const Radio = styled.span<{ isSelected: boolean; disabled?: boolean }>`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  background-color: transparent;
  transition: border-color ${fastTransition};

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      &:after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: ${theme.colors.primary};
      }
    `}

  ${({ disabled, theme }) =>
    disabled &&
    css`
      border-color: ${theme.colors.textLighter};
      &:after {
        background-color: ${theme.colors.textLighter};
      }
    `}
`

const Label = styled.span<{ isSelected: boolean; disabled?: boolean }>`
  flex: 1;
  overflow-wrap: anywhere;
  ${({ isSelected }) =>
    isSelected ? applyTextStyle('labelHeavy') : applyTextStyle('label')}
  color: ${({ theme, isSelected, disabled }) =>
    disabled
      ? theme.colors.textLighter
      : isSelected
      ? theme.colors.primary
      : theme.colors.text};
  transition: color ${fastTransition};
`

const CountBadge = styled(StyledStatusTag)`
  padding: 2px 6px;
  ${applyTextStyle('captionHeavy')}
`

const ChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};

  &:focus {
    outline: 0;
  }
`

export default FilterOptionRow
